import type { JSX } from 'react';
import { ChangeEvent, forwardRef, memo, PropsWithChildren, useMemo } from 'react';
import { IMaskInput } from 'react-imask';

import useScrollIntoView from '@/hooks/useValidateAddress/useScrollIntoView';
import Label from '@/ui/members/components/form/Label';
import { focusClasses } from '@/ui/members/components/form/styles';

import { Props } from './types';

/**
 * @deprecated Do not use this component. Use the base components at the root of the UI folder instead.
 */
const InputField = forwardRef<HTMLInputElement, PropsWithChildren<Props>>(
  (
    {
      autoComplete,
      autoFocus = false,
      children,
      disabled = false,
      helpText,
      label,
      max,
      maxLength,
      min,
      id,
      inputClassName,
      inputMode,
      onBlur,
      onChange,
      onFocus,
      pattern,
      placeholder,
      readOnly = false,
      requiredField,
      scrollIntoView = true,
      step,
      type,
      typeHelper,
      value
    },
    ref
  ) => {
    const disabledClassNames = disabled || readOnly ? 'opacity-50 cursor-not-allowed' : '';

    const handleFocus = useScrollIntoView({
      enabled: scrollIntoView,
      offset: 0,
      onFocus: onFocus
    });

    const getTypeHelper = useMemo((): string | JSX.Element | null => {
      if (typeHelper === 'phone') {
        return '+1';
      }

      return null;
    }, [typeHelper]);

    const input = (borderRadius = 'rounded-md') => {
      const className = `h-12 w-full py-2 px-3 border-0 dark:bg-uniform-gray-700 light:bg-uniform-gray-100 dark:text-uniform-gray-50 light:text-uniform-gray-900 ${focusClasses} text-base ${borderRadius} ${disabledClassNames} ${inputClassName || ''} dark:scheme-dark light:scheme-light`;

      return (
        <div className="relative flex-1">
          {type === 'tel' ? (
            <IMaskInput
              ref={ref}
              autoComplete={autoComplete}
              autoFocus={autoFocus}
              className={className}
              disabled={disabled}
              id={id}
              inputMode={inputMode}
              mask="(000) 000-0000"
              maxLength={maxLength}
              onAccept={(val: string) => {
                const event = { target: { value: val } } as Pick<
                  ChangeEvent<HTMLInputElement>,
                  'target'
                >;
                onChange(event as ChangeEvent<HTMLInputElement>);
              }}
              onBlur={onBlur}
              onFocus={handleFocus}
              pattern={pattern}
              placeholder={placeholder}
              readOnly={readOnly}
              step={step}
              type={type}
              unmask={true}
              value={value}
            />
          ) : (
            <input
              ref={ref}
              autoComplete={autoComplete}
              autoFocus={autoFocus}
              className={className}
              disabled={disabled}
              id={id}
              inputMode={inputMode}
              maxLength={maxLength}
              max={max}
              min={min}
              onBlur={onBlur}
              onChange={onChange}
              onFocus={handleFocus}
              pattern={pattern}
              placeholder={placeholder}
              readOnly={readOnly}
              step={step}
              type={type}
              value={value}
            />
          )}

          {children}
        </div>
      );
    };

    const renderInput = () => {
      if (typeHelper) {
        return (
          <div className="flex items-center">
            <div className="h-12 w-14 dark:bg-uniform-gray-600 light:bg-uniform-gray-200 pointer-events-none rounded-l-md flex items-center justify-center">
              <span className="dark:text-uniform-gray-50">{getTypeHelper}</span>
            </div>

            {input('rounded-l-none rounded-r-md')}
          </div>
        );
      }

      return input();
    };

    return (
      <div>
        {label && <Label htmlFor={id} text={label} />}

        {helpText && <p className="text-sm dark:text-uniform-gray-600">{helpText}</p>}

        {renderInput()}

        {requiredField}
      </div>
    );
  }
);

InputField.displayName = 'InputField';

export default memo(InputField);
